import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import {
    FiRefreshCcw,
    FiPlus,
    FiMinus,
    FiTrash2,
    FiCheck,
    FiX,
} from 'react-icons/fi';
import './PosPageStaff.css';
import { apiFetch } from '../../api/client';
import logger from '../../utils/logger';
import {
    fetchPosDashboard,
    fetchPosOrderDetail,
    cancelPosOrder,
    PosDashboard,
    PosOrderDetail,
    PosMenuItem,
    PosMenuCategory,
    PosMenuVariant,
    PosOrderPaymentPayload,
    registerPosOrderPayment,
    createPosOrder,
    CreatePosOrderPayload,
    addItemsToPosOrder,
} from '../../api/posApi';

const currencyFormat = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });
const formatCurrency = (value?: number | null) => currencyFormat.format(value || 0);

const toNumber = (value?: number | string | null) => {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
    }
    if (typeof value === 'string') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
};

const roundCurrency = (value: number) => {
    if (!Number.isFinite(value)) {
        return 0;
    }
    return Number(value.toFixed(2));
};

const buildVariantKey = (variant: PosMenuVariant | null | undefined): string => {
    if (!variant) {
        return 'variant:unknown';
    }
    if (variant.id !== null && variant.id !== undefined) {
        return `id:${variant.id}`;
    }
    const normalizedName = (variant.name ?? '').trim().toLowerCase();
    const priceToken = roundCurrency(toNumber(variant.price));
    return `name:${normalizedName}|price:${priceToken}`;
};

const createCartKey = () => `cart-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

const formatTime = (dateString?: string | null) => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    } catch {
        return '';
    }
};

interface CartItem {
    key: string;
    menuItemId: number;
    baseName: string;
    name: string;
    price: number;
    basePrice: number;
    quantity: number;
    notes?: string; // Note/commenti personalizzati per l'item
    variantOptions?: PosMenuVariant[];
    isVariant?: boolean;
    parentKey?: string | null;
    variantId?: number | null;
    variantKey?: string | null;
    variantName?: string | null;
}

/**
 * POS Page Staff - Versione semplificata mobile-friendly per staff
 * Focus: Prendere ordini velocemente senza confusione
 */
const PosPageStaff: React.FC = () => {
    const [dashboard, setDashboard] = useState<PosDashboard | null>(null);
    const [loading, setLoading] = useState(true);
    const [cart, setCart] = useState<CartItem[]>([]);
    const [selectedTable, setSelectedTable] = useState<string | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
    const [orderSubmitting, setOrderSubmitting] = useState(false);
    const [showCategorySelect, setShowCategorySelect] = useState(true);
    const [customTableNumber, setCustomTableNumber] = useState('');
    const [showCustomProductModal, setShowCustomProductModal] = useState(false);
    const [customProductName, setCustomProductName] = useState('');
    const [customProductPrice, setCustomProductPrice] = useState('');
    const [existingOrderId, setExistingOrderId] = useState<number | null>(null);
    const [viewingOrderDetails, setViewingOrderDetails] = useState(false);
    const [orderDetails, setOrderDetails] = useState<PosOrderDetail | null>(null);
    const pollingRef = useRef<NodeJS.Timeout | null>(null);
    const [orderComment, setOrderComment] = useState('');
    const [variantSelections, setVariantSelections] = useState<Record<string, string>>({});

    // 🆕 Nuovo flusso: modalità shopping e modal carrello
    const [isShoppingMode, setIsShoppingMode] = useState(false); // true = mostra prodotti
    const [showCartModal, setShowCartModal] = useState(false); // Modal carrello
    const [searchQuery, setSearchQuery] = useState(''); // Ricerca prodotti

    // 🍽️ Coperto States
    const [coverChargeModalOpen, setCoverChargeModalOpen] = useState(false);
    const [coverChargeAmount, setCoverChargeAmount] = useState<number>(0);
    const [numberOfPeople, setNumberOfPeople] = useState<string>('2');

    const loadDashboard = useCallback(async () => {
        try {
            const data = await fetchPosDashboard();
            setDashboard(data);
        } catch (error) {
            console.error('Error loading dashboard:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadDashboard();

        // Polling ogni 30 secondi
        pollingRef.current = setInterval(() => {
            loadDashboard();
        }, 30000);

        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
            }
        };
    }, [loadDashboard]);

    // 🍽️ Carica importo coperto
    useEffect(() => {
        const loadCoverCharge = async () => {
            try {
                const response = await apiFetch('/api/tenant-settings');
                if (response.ok) {
                    const settings = await response.json();
                    setCoverChargeAmount(settings.coverCharge || 0);
                    logger.debug('Cover charge loaded');
                } else {
                    logger.error('Failed to load cover charge');
                }
            } catch (error) {
                logger.error('Failed to load cover charge:', error);
            }
        };
        loadCoverCharge();
    }, []);

    // 🍽️ Gestione modale coperto (manuale)
    const openCoverChargeModal = useCallback(() => {
        if (coverChargeAmount > 0) {
            setNumberOfPeople('2');
            setCoverChargeModalOpen(true);
        }
    }, [coverChargeAmount]);

    const handleCoverChargeConfirm = useCallback(() => {
        const people = parseInt(numberOfPeople, 10);
        if (people > 0 && coverChargeAmount > 0) {
            const totalCoverCharge = roundCurrency(people * coverChargeAmount);
            const coverItem: CartItem = {
                key: createCartKey(),
                menuItemId: -1, // ID speciale per coperto
                baseName: `Coperto (${people} ${people === 1 ? 'persona' : 'persone'})`,
                name: `Coperto (${people} ${people === 1 ? 'persona' : 'persone'})`,
                price: totalCoverCharge,
                basePrice: totalCoverCharge,
                quantity: 1,
                isVariant: false,
                parentKey: null,
                variantId: null,
                variantKey: null,
                variantName: null,
            };
            setCart(prev => [coverItem, ...prev]);
        }
        setCoverChargeModalOpen(false);
        setNumberOfPeople('2');
    }, [numberOfPeople, coverChargeAmount]);

    const handleCoverChargeSkip = useCallback(() => {
        setCoverChargeModalOpen(false);
        setNumberOfPeople('2');
    }, []);

    const clearCart = () => {
        setCart([]);
        setSelectedTable(null);
        setSelectedCategory(null);
        setShowCategorySelect(true);
        setExistingOrderId(null);
        setViewingOrderDetails(false);
        setOrderDetails(null);
        setOrderComment('');
        setIsShoppingMode(false);
        setShowCartModal(false);
        setSearchQuery('');
        setVariantSelections({});
    };

    const loadExistingOrder = async (orderId: number, tableNumber: string) => {
        try {
            setLoading(true);
            const details = await fetchPosOrderDetail(orderId);
            setOrderDetails(details);
            setExistingOrderId(orderId);
            setSelectedTable(tableNumber);
            setViewingOrderDetails(true);
            setIsShoppingMode(false); // Mostra dettagli ordine
        } catch (error) {
            console.error('Error loading order details:', error);
            alert('Errore nel caricamento dei dettagli dell\'ordine');
        } finally {
            setLoading(false);
        }
    };

    const addMoreItemsToExistingOrder = () => {
        setViewingOrderDetails(false);
        setShowCategorySelect(true);
        setIsShoppingMode(true); // Entra in modalità shopping
        setOrderComment('');
        // Il carrello parte vuoto, verranno aggiunti nuovi prodotti
    };

    const startNewOrder = (tableNumber: string) => {
        setSelectedTable(tableNumber);
        setIsShoppingMode(true);
        setShowCategorySelect(true);
        setCart([]);
    };

    const addToCart = (item: PosMenuItem) => {
        const rawId = item.id;
        if (rawId == null || !item.name) return;

        const normalizedName = item.name.trim();
        const baseName = normalizedName.length > 0 ? normalizedName : 'Articolo';
        const basePrice = roundCurrency(toNumber(item.counterPrice ?? item.price ?? 0));
        const variantCandidates = Array.isArray(item.variants)
            ? item.variants.filter((variant): variant is PosMenuVariant => Boolean(variant) && variant.available !== false)
            : [];
        const variantOptions = variantCandidates
            .slice()
            .sort((a, b) => {
                const posA = a.position ?? Number.MAX_SAFE_INTEGER;
                const posB = b.position ?? Number.MAX_SAFE_INTEGER;
                if (posA !== posB) {
                    return posA - posB;
                }
                const idA = a.id ?? Number.MAX_SAFE_INTEGER;
                const idB = b.id ?? Number.MAX_SAFE_INTEGER;
                return idA - idB;
            });

        setCart(prev => {
            const next = [...prev];
            const existingIndex = next.findIndex(entry => entry.menuItemId === rawId && !entry.isVariant);
            if (existingIndex >= 0) {
                const existing = next[existingIndex];
                next[existingIndex] = {
                    ...existing,
                    key: existing.key ?? createCartKey(),
                    quantity: Math.min(existing.quantity + 1, 99),
                    baseName,
                    basePrice,
                    price: basePrice,
                    variantOptions: existing.variantOptions ?? (variantOptions.length > 0 ? variantOptions : undefined),
                    isVariant: false,
                    parentKey: null,
                    variantId: null,
                    variantKey: null,
                    variantName: null,
                };
                return next;
            }

            const key = createCartKey();
            const newItem: CartItem = {
                key,
                menuItemId: rawId,
                baseName,
                name: baseName,
                price: basePrice,
                basePrice,
                quantity: 1,
                notes: undefined,
                variantOptions: variantOptions.length > 0 ? variantOptions : undefined,
                isVariant: false,
                parentKey: null,
                variantId: null,
                variantKey: null,
                variantName: null,
            };
            next.push(newItem);
            return next;
        });
    };

    const addCustomProductToCart = () => {
        if (!customProductName.trim() || !customProductPrice.trim()) {
            alert('Inserisci nome e prezzo del prodotto');
            return;
        }

        const price = roundCurrency(parseFloat(customProductPrice));
        if (isNaN(price) || price < 0) {
            alert('Inserisci un prezzo valido');
            return;
        }

        // Usa un ID negativo per prodotti custom (non conflitto con prodotti reali)
        const customId = -(Date.now());
        const baseName = customProductName.trim();

        setCart([...cart, {
            key: createCartKey(),
            menuItemId: customId,
            baseName,
            name: baseName,
            price: price,
            basePrice: price,
            quantity: 1,
            isVariant: false,
            parentKey: null,
            variantId: null,
            variantKey: null,
            variantName: null,
        }]);

        // Reset modal
        setCustomProductName('');
        setCustomProductPrice('');
        setShowCustomProductModal(false);
    };

    const updateCartQuantity = (key: string, delta: number) => {
        setCart(prev => {
            const index = prev.findIndex(item => item.key === key);
            if (index === -1) {
                return prev;
            }
            const entry = prev[index];
            const nextQuantity = entry.quantity + delta;
            if (nextQuantity <= 0) {
                if (entry.isVariant) {
                    return prev.filter(item => item.key !== key);
                }
                return prev.filter(item => item.key !== key && item.parentKey !== key);
            }
            const quantity = Math.min(99, Math.max(1, nextQuantity));
            const next = [...prev];
            next[index] = { ...entry, quantity };
            return next;
        });
    };

    const updateCartNotes = (key: string, notes: string) => {
        setCart(prev => {
            const index = prev.findIndex(item => item.key === key);
            if (index === -1) {
                return prev;
            }
            const next = [...prev];
            next[index] = { ...next[index], notes };
            return next;
        });
    };

    const addVariantToCart = useCallback((parentKey: string, variantKey: string) => {
        if (!variantKey) {
            return;
        }
        setCart(prev => {
            const parentIndex = prev.findIndex(item => item.key === parentKey && !item.isVariant);
            if (parentIndex === -1) {
                return prev;
            }
            const parent = prev[parentIndex];
            const options = parent.variantOptions ?? [];
            const selected = options.find(option => buildVariantKey(option) === variantKey);
            if (!selected) {
                return prev;
            }
            const variantName = (selected.name ?? '').trim() || 'Variante';
            const variantPrice = roundCurrency(toNumber(selected.price));
            const effectivePrice = variantPrice > 0 ? variantPrice : roundCurrency(parent.basePrice);
            const existingIndex = prev.findIndex(item => item.isVariant && item.parentKey === parentKey && item.variantKey === variantKey);
            if (existingIndex >= 0) {
                const next = [...prev];
                const existing = next[existingIndex];
                next[existingIndex] = {
                    ...existing,
                    quantity: Math.min(existing.quantity + 1, 99),
                    price: effectivePrice,
                };
                return next;
            }
            const variantItem: CartItem = {
                key: createCartKey(),
                menuItemId: parent.menuItemId,
                baseName: parent.baseName,
                name: `${parent.baseName} (${variantName})`,
                price: effectivePrice,
                basePrice: effectivePrice,
                quantity: 1,
                isVariant: true,
                parentKey,
                variantId: selected.id ?? null,
                variantKey,
                variantName,
            };
            const next = [...prev];
            next.splice(parentIndex + 1, 0, variantItem);
            return next;
        });
        setVariantSelections(prev => ({ ...prev, [parentKey]: '' }));
    }, []);

    const removeFromCart = (key: string) => {
        setCart(prev => {
            const entry = prev.find(item => item.key === key);
            if (!entry) {
                return prev;
            }
            if (entry.isVariant) {
                return prev.filter(item => item.key !== key);
            }
            return prev.filter(item => item.key !== key && item.parentKey !== key);
        });
        setVariantSelections(prev => {
            if (!(key in prev)) {
                return prev;
            }
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    const cartTotal = useMemo(() => {
        return roundCurrency(cart.reduce((sum, item) => {
            const unitPrice = roundCurrency(item.price);
            const quantity = Math.max(1, item.quantity);
            return sum + unitPrice * quantity;
        }, 0));
    }, [cart]);

    useEffect(() => {
        if (cart.length === 0 && orderComment !== '') {
            setOrderComment('');
        }
    }, [cart.length, orderComment]);

    useEffect(() => {
        setVariantSelections(prev => {
            const next: Record<string, string> = {};
            let changed = false;
            cart.forEach(item => {
                if (!item.isVariant && item.variantOptions && item.variantOptions.length > 0) {
                    const existing = prev[item.key];
                    if (existing !== undefined) {
                        next[item.key] = existing;
                        if (existing !== prev[item.key]) {
                            changed = true;
                        }
                    } else {
                        next[item.key] = '';
                        changed = true;
                    }
                }
            });
            Object.keys(prev).forEach(key => {
                const stillExists = cart.some(item => !item.isVariant && item.key === key && item.variantOptions && item.variantOptions.length > 0);
                if (!stillExists) {
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [cart]);

    const handleSubmitOrder = async () => {
        if (!selectedTable || cart.length === 0) return;

        setOrderSubmitting(true);
        try {
            const items = cart.map(item => {
                const trimmedNote = item.notes?.trim();
                return {
                    menuItemId: item.menuItemId > 0 ? item.menuItemId : null,
                    name: item.name,
                    price: roundCurrency(item.price),
                    quantity: item.quantity,
                    custom: item.menuItemId < 0, // Prodotti personalizzati hanno ID negativo
                    comment: trimmedNote && trimmedNote.length > 0 ? trimmedNote : undefined,
                };
            });

            const trimmedOrderComment = orderComment.trim();
            if (trimmedOrderComment.length > 0) {
                items.push({
                    menuItemId: null,
                    name: 'Nota ordine',
                    price: 0,
                    quantity: 1,
                    custom: true,
                    comment: trimmedOrderComment,
                });
            }

            if (existingOrderId && orderDetails?.code) {
                // Aggiungi prodotti a ordine esistente
                await addItemsToPosOrder(orderDetails.code, items);
                alert('Prodotti aggiunti all\'ordine con successo!');
            } else {
                // Crea nuovo ordine
                const payload: CreatePosOrderPayload = {
                    tableNumber: selectedTable,
                    items,
                };
                await createPosOrder(payload);
                alert('Ordine inviato con successo!');
            }

            clearCart();
            await loadDashboard();
        } catch (error) {
            console.error('Error submitting order:', error);
            alert('Errore durante l\'invio dell\'ordine');
        } finally {
            setOrderSubmitting(false);
        }
    };

    const selectedCategoryData = dashboard?.menu?.find(cat => cat.id === selectedCategory);
    const currentMenuItems = selectedCategoryData?.items || [];

    // Filtro prodotti con ricerca
    const filteredMenuItems = currentMenuItems.filter(item => {
        if (!searchQuery.trim()) return true;
        return item.name?.toLowerCase().includes(searchQuery.toLowerCase());
    });

    const occupiedTableNumbers = dashboard?.tables?.filter(t => t.currentOrder !== null).map(t => t.tableNumber || '') || [];
    const occupiedTablesCount = dashboard?.tables?.filter(t => t.currentOrder !== null).length ?? 0;
    const allTables = Array.from({ length: 20 }, (_, i) => String(i + 1));
    const freeTables = allTables.filter(n => !occupiedTableNumbers.includes(n));

    const summaryMetrics: Array<{ label: string; value: string }> = [];
    if (typeof dashboard?.openOrders === 'number') {
        summaryMetrics.push({ label: 'Ordini aperti', value: String(dashboard.openOrders) });
    }
    if (typeof dashboard?.todaysTotal === 'number') {
        summaryMetrics.push({ label: 'Incasso oggi', value: formatCurrency(dashboard.todaysTotal) });
    }
    summaryMetrics.push({ label: 'Tavoli occupati', value: String(occupiedTablesCount) });

    if (loading) {
        return <div className="pos-staff-loading">Caricamento...</div>;
    }

    return (
        <div className="pos-staff-container">
            <header className="pos-staff-topbar">
                <div className="pos-staff-topbar__brand">
                    <span className="pos-staff-topbar__badge">POS</span>
                    <div className="pos-staff-topbar__titles">
                        <h1>Staff Touch</h1>
                        <p>Ordini rapidi per il personale</p>
                    </div>
                </div>
                {summaryMetrics.length > 0 && (
                    <div className="pos-staff-topbar__metrics">
                        {summaryMetrics.map(metric => (
                            <div key={metric.label} className="pos-staff-topbar__metric">
                                <span>{metric.label}</span>
                                <strong>{metric.value}</strong>
                            </div>
                        ))}
                    </div>
                )}
                <div className="pos-staff-topbar__actions">
                    <button onClick={loadDashboard} className="btn-refresh" title="Aggiorna">
                        <FiRefreshCcw />
                    </button>
                </div>
            </header>

            <main className="pos-staff-content">
                {!selectedTable && (
                    <section className="table-selection pos-staff-section">
                        <h2>Seleziona Tavolo</h2>

                        <div className="custom-table-input">
                            <input
                                type="text"
                                placeholder="Es: 15, A5, Terrazza..."
                                value={customTableNumber}
                                onChange={(e) => setCustomTableNumber(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' && customTableNumber.trim()) {
                                        startNewOrder(customTableNumber.trim());
                                        setCustomTableNumber('');
                                    }
                                }}
                            />
                            <button
                                className="btn-create-table"
                                onClick={() => {
                                    if (customTableNumber.trim()) {
                                        startNewOrder(customTableNumber.trim());
                                        setCustomTableNumber('');
                                    }
                                }}
                                disabled={!customTableNumber.trim()}
                            >
                                <FiPlus /> Crea Tavolo
                            </button>
                        </div>

                        <div className="tables-grid">
                            {freeTables.map(num => (
                                <button
                                    key={num}
                                    className="table-btn free"
                                    onClick={() => startNewOrder(num)}
                                >
                                    {num}
                                </button>
                            ))}
                        </div>
                        {dashboard?.tables && dashboard.tables.filter(t => t.currentOrder).length > 0 && (
                            <>
                                <h3>Tavoli Occupati</h3>
                                <div className="occupied-tables-grid">
                                    {dashboard.tables.filter(t => t.currentOrder).map((table, idx) => (
                                        <button
                                            key={idx}
                                            className="table-btn occupied"
                                            onClick={() => {
                                                if (table.currentOrder?.id && table.tableNumber) {
                                                    loadExistingOrder(table.currentOrder.id, table.tableNumber);
                                                }
                                            }}
                                        >
                                            <div className="table-number">{table.tableNumber}</div>
                                            <div className="table-total">{formatCurrency(table.currentOrder?.total)}</div>
                                            <small className="order-code">#{table.currentOrder?.code || table.currentOrder?.id}</small>
                                            {table.currentOrder?.openedAt && (
                                                <small className="order-time">
                                                    🕐 {formatTime(table.currentOrder.openedAt)}
                                                </small>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </section>
                )}

                {viewingOrderDetails && orderDetails && (
                    <section className="order-details-view pos-staff-section">
                        <div className="active-table-bar">
                            <div>
                                <strong>Tavolo {selectedTable}</strong>
                                <span>Ordine #{orderDetails.code}</span>
                            </div>
                            <button onClick={clearCart} className="btn-cancel">
                                <FiX /> Chiudi
                            </button>
                        </div>

                        <div className="order-details-content">
                            <div className="order-summary">
                                <h3>Riepilogo Ordine</h3>
                                <div className="order-meta">
                                    <span>Totale: <strong>{formatCurrency(orderDetails.total)}</strong></span>
                                    {orderDetails.openedAt && (
                                        <span>Aperto alle: {formatTime(orderDetails.openedAt)}</span>
                                    )}
                                </div>
                            </div>

                            <div className="order-items-list">
                                <h4>Prodotti nell'ordine</h4>
                                {orderDetails.items && orderDetails.items.length > 0 ? (
                                    orderDetails.items.map((item, idx) => (
                                        <div key={item.id || idx} className="order-item-row">
                                            <div className="item-info">
                                                <strong>{item.name}</strong>
                                                <span className="item-price">{formatCurrency(item.price)}</span>
                                                {item.comment && (
                                                    <span className="item-note">📝 {item.comment}</span>
                                                )}
                                            </div>
                                            <div className="item-quantity">x{item.quantity}</div>
                                            <div className="item-total">{formatCurrency(item.total)}</div>
                                        </div>
                                    ))
                                ) : (
                                    <p>Nessun prodotto nell'ordine</p>
                                )}
                            </div>

                            <button
                                className="btn-add-more"
                                onClick={addMoreItemsToExistingOrder}
                            >
                                <FiPlus /> Aggiungi Altri Prodotti
                            </button>
                        </div>
                    </section>
                )}

                {/* Mostra categorie SOLO in shopping mode (quando aggiungi prodotti) */}
                {isShoppingMode && selectedTable && !selectedCategory && showCategorySelect && (
                    <section className="pos-staff-section">
                        <div className="active-table-bar">
                            <div>
                                <strong>Tavolo {selectedTable}</strong>
                                <span>Seleziona categoria</span>
                            </div>
                            <button onClick={clearCart} className="btn-cancel">
                                <FiX /> Annulla
                            </button>
                        </div>

                        <div className="category-grid-container">
                            <h2>Seleziona Categoria</h2>
                            <div className="category-grid">
                                {dashboard?.menu?.map((cat, idx) => (
                                    <button
                                        key={cat.id || idx}
                                        className="category-card"
                                        onClick={() => {
                                            setSelectedCategory(cat.id || null);
                                            setShowCategorySelect(false);
                                        }}
                                    >
                                        <div className="category-name">{cat.name}</div>
                                        <div className="category-count">{cat.items?.length || 0} prodotti</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* Mostra prodotti SOLO in shopping mode */}
                {isShoppingMode && selectedTable && selectedCategory && (
                    <>
                        <section className="active-table-bar pos-staff-section">
                            <div>
                                <strong>Tavolo {selectedTable}</strong>
                                <span>{selectedCategoryData?.name} - {cart.length} articoli</span>
                            </div>
                            <div className="active-table-actions">
                                <button
                                    onClick={() => {
                                        setSelectedCategory(null);
                                        setShowCategorySelect(true);
                                    }}
                                    className="btn-change-category"
                                >
                                    🔄 Cambia Categoria
                                </button>
                                <button onClick={clearCart} className="btn-cancel">
                                    <FiX /> Annulla
                                </button>
                            </div>
                        </section>

                        {/* Tabs Categorie per cambio rapido */}
                        <section className="category-tabs">
                            {dashboard?.menu?.map((cat, idx) => (
                                <button
                                    key={cat.id || idx}
                                    className={`category-tab ${cat.id === selectedCategory ? 'active' : ''}`}
                                    onClick={() => {
                                        setSelectedCategory(cat.id || null);
                                        setSearchQuery(''); // Reset ricerca quando cambi categoria
                                    }}
                                >
                                    {cat.name}
                                </button>
                            ))}
                        </section>

                        {/* Barra di ricerca prodotti */}
                        <section className="search-bar-section">
                            <input
                                type="text"
                                className="product-search-input"
                                placeholder="🔍 Cerca prodotto..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </section>

                        <section className="menu-items-grid-fullscreen">
                            {filteredMenuItems.map((item, idx) => (
                                <div key={item.id || idx} className="menu-item-card">
                                    <div className="item-info">
                                        <strong>{item.name}</strong>
                                        <span className="item-price">
                                            {formatCurrency(item.counterPrice || item.price)}
                                        </span>
                                    </div>
                                    <button
                                        className="add-btn"
                                        onClick={() => addToCart(item)}
                                    >
                                        <FiPlus /> Aggiungi
                                    </button>
                                </div>
                            ))}

                            <div className="menu-item-card custom-product-card">
                                <div className="item-info">
                                    <strong>➕ Prodotto Personalizzato</strong>
                                    <span className="item-price">Crea al volo</span>
                                </div>
                                <button
                                    className="add-btn custom"
                                    onClick={() => setShowCustomProductModal(true)}
                                >
                                    <FiPlus /> Crea
                                </button>
                            </div>

                            {coverChargeAmount > 0 && (
                                <div className="menu-item-card custom-product-card" style={{ borderColor: '#10b981' }}>
                                    <div className="item-info">
                                        <strong>🍽️ Coperto</strong>
                                        <span className="item-price">€{coverChargeAmount.toFixed(2)} / persona</span>
                                    </div>
                                    <button
                                        className="add-btn"
                                        onClick={openCoverChargeModal}
                                        style={{ background: '#10b981', borderColor: '#059669' }}
                                    >
                                        <FiPlus /> Aggiungi
                                    </button>
                                </div>
                            )}
                        </section>

                        {/* Carrello rimosso - ora è un modal flottante */}
                    </>
                )}
            </main>

            {/* Modal Prodotto Personalizzato */}
            {showCustomProductModal && (
                <div className="modal-overlay" onClick={() => setShowCustomProductModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>➕ Crea Prodotto Personalizzato</h3>
                            <button onClick={() => setShowCustomProductModal(false)} className="modal-close">
                                <FiX />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Nome Prodotto</label>
                                <input
                                    type="text"
                                    placeholder="Es: Pizza Speciale, Cocktail..."
                                    value={customProductName}
                                    onChange={(e) => setCustomProductName(e.target.value)}
                                    autoFocus
                                />
                            </div>
                            <div className="form-group">
                                <label>Prezzo (€)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder="Es: 12.50"
                                    value={customProductPrice}
                                    onChange={(e) => setCustomProductPrice(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            addCustomProductToCart();
                                        }
                                    }}
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button onClick={() => setShowCustomProductModal(false)} className="btn-cancel">
                                Annulla
                            </button>
                            <button onClick={addCustomProductToCart} className="btn-confirm">
                                <FiCheck /> Aggiungi al Carrello
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 🍽️ Modal Coperto */}
            {coverChargeModalOpen && (
                <div className="modal-overlay" onClick={handleCoverChargeSkip}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
                        <div className="modal-header">
                            <h3>🍽️ Coperto</h3>
                            <button onClick={handleCoverChargeSkip} className="modal-close">
                                <FiX />
                            </button>
                        </div>
                        <div className="modal-body">
                            <p style={{ marginBottom: '20px', textAlign: 'center', fontSize: '1.1rem' }}>
                                Quante persone?
                            </p>
                            <div className="form-group">
                                <label style={{ fontSize: '1.1rem', fontWeight: '600' }}>Numero persone</label>
                                <input
                                    type="number"
                                    inputMode="numeric"
                                    min="0"
                                    max="99"
                                    value={numberOfPeople}
                                    onChange={(e) => setNumberOfPeople(e.target.value)}
                                    style={{
                                        fontSize: '2rem',
                                        textAlign: 'center',
                                        padding: '16px',
                                        fontWeight: 'bold'
                                    }}
                                    autoFocus
                                />
                            </div>
                            <div style={{
                                marginTop: '16px',
                                padding: '12px',
                                background: '#f3f4f6',
                                borderRadius: '8px',
                                textAlign: 'center'
                            }}>
                                <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '4px' }}>
                                    Totale coperto
                                </div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827' }}>
                                    €{((parseInt(numberOfPeople) || 0) * coverChargeAmount).toFixed(2)}
                                </div>
                                <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginTop: '4px' }}>
                                    (€{coverChargeAmount.toFixed(2)} × {numberOfPeople || 0} {parseInt(numberOfPeople) === 1 ? 'persona' : 'persone'})
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button onClick={handleCoverChargeSkip} className="btn-cancel">
                                Salta
                            </button>
                            <button
                                onClick={handleCoverChargeConfirm}
                                className="btn-confirm"
                                disabled={!numberOfPeople || parseInt(numberOfPeople) <= 0}
                            >
                                <FiCheck /> Conferma coperto
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 🛒 Pulsante Flottante Ordine in Corso */}
            {isShoppingMode && cart.length > 0 && (
                <button
                    className="floating-cart-btn"
                    onClick={() => setShowCartModal(true)}
                >
                    <span className="cart-icon">🛒</span>
                    <span className="cart-count">{cart.length}</span>
                    <span className="cart-total-float">{formatCurrency(cartTotal)}</span>
                </button>
            )}

            {/* 🛒 Modal Carrello */}
            {showCartModal && (
                <div className="cart-modal-overlay" onClick={() => setShowCartModal(false)}>
                    <div className="cart-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="cart-modal-header">
                            <h3>Ordine in Corso - Tavolo {selectedTable}</h3>
                            <button onClick={() => setShowCartModal(false)} className="modal-close">
                                <FiX />
                            </button>
                        </div>

                        <div className="cart-modal-body">
                            {cart.map(item => {
                                const isVariant = Boolean(item.isVariant);
                                const variantOptions = !isVariant ? item.variantOptions ?? [] : [];
                                const selectValue = variantSelections[item.key] ?? '';
                                const wrapperStyle = isVariant
                                    ? { background: '#f9fafb', borderLeft: '3px solid #d1d5db' }
                                    : undefined;
                                const unitPrice = roundCurrency(item.price);
                                const itemTotal = formatCurrency(roundCurrency(unitPrice * Math.max(1, item.quantity)));
                                return (
                                    <div key={item.key} className="cart-modal-item" style={wrapperStyle}>
                                        <div className="cart-modal-item-info">
                                            <strong>{item.name}</strong>
                                            <span className="cart-modal-item-price">{itemTotal}</span>
                                        </div>
                                        {!isVariant && variantOptions.length > 0 && (
                                            <label className="cart-modal-item-variant" style={{ display: 'flex', flexDirection: 'column', gap: '4px', margin: '8px 0' }}>
                                                <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>Aggiungi variante</span>
                                                <select
                                                    value={selectValue}
                                                    onChange={(event) => addVariantToCart(item.key, event.target.value)}
                                                    className="cart-modal-item-select"
                                                    style={{
                                                        padding: '8px 10px',
                                                        borderRadius: '8px',
                                                        border: '1px solid #d1d5db',
                                                        fontSize: '0.95rem',
                                                    }}
                                                >
                                                    <option value="">+ Aggiungi variante</option>
                                                    {variantOptions.map(option => {
                                                        const optionKey = buildVariantKey(option);
                                                        const optionName = (option.name ?? '').trim() || 'Variante';
                                                        const optionPrice = roundCurrency(toNumber(option.price));
                                                        const priceLabel = formatCurrency(optionPrice > 0 ? optionPrice : item.basePrice);
                                                        return (
                                                            <option key={optionKey} value={optionKey}>
                                                                {optionName} – {priceLabel}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                            </label>
                                        )}
                                        <div className="cart-modal-item-controls">
                                            <button onClick={() => updateCartQuantity(item.key, -1)} className="cart-modal-btn">
                                                <FiMinus />
                                            </button>
                                            <span className="cart-modal-qty">{item.quantity}</span>
                                            <button onClick={() => updateCartQuantity(item.key, 1)} className="cart-modal-btn">
                                                <FiPlus />
                                            </button>
                                            <button onClick={() => removeFromCart(item.key)} className="cart-modal-btn-remove">
                                                <FiTrash2 />
                                            </button>
                                        </div>
                                        <input
                                            type="text"
                                            className="cart-modal-item-notes"
                                            placeholder="Note (es: senza cipolla...)"
                                            value={item.notes || ''}
                                            onChange={(e) => updateCartNotes(item.key, e.target.value)}
                                        />
                                    </div>
                                );
                            })}

                            {/* Commento ordine */}
                            <div className="cart-modal-comment">
                                <label htmlFor="cart-modal-comment-input">Indicazioni per il banco</label>
                                <textarea
                                    id="cart-modal-comment-input"
                                    className="cart-modal-comment-input"
                                    placeholder="Es: consegnare fuori, allergie, priorità di servizio..."
                                    value={orderComment}
                                    onChange={(e) => setOrderComment(e.target.value)}
                                    rows={2}
                                />
                            </div>
                        </div>

                        <div className="cart-modal-footer">
                            <div className="cart-modal-total">
                                <span>Totale:</span>
                                <strong>{formatCurrency(cartTotal)}</strong>
                            </div>
                            <button
                                className="cart-modal-submit-btn"
                                onClick={handleSubmitOrder}
                                disabled={orderSubmitting || cart.length === 0}
                            >
                                {orderSubmitting ? 'Invio...' : '✓ Invia Ordine'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PosPageStaff;
